import React from 'react';

import { BANK_CODES, getOrganizationLogo, ShortedBankName } from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { Convert } from '@revenuemarket/react';
import { Toast } from '@revenuemarket/ui';
import { Alert, Button, Card, List, Typography } from 'antd';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { ExternalCredentialTarget } from '~apis/external-credential';
import {
  CARD_SCRAPE_TYPE_ORDER,
  ICashReceiptScrapingStatusItem,
  ITaxInvoiceScrapingStatusItem,
  postBillingScraping,
} from '~apis/scrape';
import ListForCorpCard from '~components/ScrapeStatus/ListForCorpCard';
import ListForCrefia from '~components/ScrapeStatus/ListForCrefia';
import {
  getAnalyzeStatusTag,
  HomeTaxEntityType,
  ListForHometax,
} from '~components/ScrapeStatus/ListForHometax';
import ScrapeErrorsDrawer from '~components/ui/layout/ScrapeErrorsDrawer';
import { useGetExternalIdPasswordCredentials } from '~hooks/queries/crefia/useExternalIdPasswordCredential';
import useGetIntegratedScrapeStatus from '~hooks/queries/scraping/useGetIntegratedScrapeStatus';
import useCorporateCardConnections from '~hooks/queries/useCorporateCardConnections';
import { useCompanyId } from '~hooks/useCompanyId';
import { css } from '~libs/emotion';

interface AnalyzeDashboardProps {
  onScrape: () => void;
}

const getHomeTaxEntityTypeFromTaxInvoice = (
  item: ITaxInvoiceScrapingStatusItem,
): HomeTaxEntityType => {
  if (item.type === 'SALES') {
    return 'TAX_INVOICE_SALES';
  }
  return 'TAX_INVOICE_PURCHASE';
};

const getHomeTaxEntityTypeFromCashReceipt = (
  item: ICashReceiptScrapingStatusItem,
): HomeTaxEntityType => {
  if (item.type === 'SALES') {
    return 'CASH_RECEIPT_SALES';
  }
  return 'CASH_RECEIPT_PURCHASE';
};

export const AnalyzeDashboard: React.FC<AnalyzeDashboardProps> = () => {
  const { companyId } = useCompanyId();

  const { data: scrapeStatusData } = useGetIntegratedScrapeStatus(companyId);
  const { data: cardConnectionData } = useCorporateCardConnections(companyId);
  const { data: externalCredentialData } = useGetExternalIdPasswordCredentials(companyId);

  const scrapeStatus = scrapeStatusData?.Data?.data;
  const {
    overallStatus,
    cashReceiptScrapingStatus: cashReceipt = [],
    bankAccountScrapingStatus: bank = [],
    taxInvoiceScrapingStatus: taxInvoice = [],
    cardScrapingStatus: card = [],
    ntsCertificates: ntsCertificates = [],
    securityAccountListScrapingStatus: securityAccountList = [],
    purchasedCardSalesScrapingStatus: purchasedCardSalesList = [],
  } = scrapeStatus ?? {};

  const cardConnections = cardConnectionData?.Data?.data?.connections || [];
  const crefiaCredential =
    (externalCredentialData?.Data?.data || []).find(
      (credential) => credential.target === ExternalCredentialTarget.CREFIA,
    ) || null;

  const abnormalAccounts = scrapeStatus?.abnormalBankAccounts ?? [];
  const abnormalSecurityAccounts = scrapeStatus?.abnormalSecurityAccounts ?? [];
  const hasAbnormalAccounts = abnormalAccounts != null && abnormalAccounts.length > 0;
  const bankDataExist = bank.length > 0 || hasAbnormalAccounts;
  const taxInvoiceDataExist = taxInvoice.length > 0;
  const cashInvoiceDataExist = cashReceipt.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {bankDataExist && (
        <Card title="은행 계좌" bordered={false}>
          <AnimatePresence mode="popLayout">
            {hasAbnormalAccounts && (
              <motion.div
                key="abnormal-accounts"
                initial={{ opacity: 0, y: -24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                className={css`
                  padding-bottom: 16px;
                `}
              >
                <Alert
                  message="계좌 정보를 찾을 수 없습니다"
                  description={
                    <>
                      수집할 수 없는 계좌가 {abnormalAccounts?.length}개 있습니다.
                      <br />
                      자세히 버튼을 클릭하여 필요한 조치를 취해주세요.
                    </>
                  }
                  type="error"
                  showIcon
                  banner
                  action={
                    <Flex gap={4} align="center">
                      <Button
                        size="small"
                        onClick={() => {
                          overlays.open(({ isOpen, onClose }) => (
                            <ScrapeErrorsDrawer
                              isOpen={isOpen}
                              onClose={onClose}
                              accountType="bank"
                            />
                          ));
                        }}
                      >
                        자세히
                      </Button>
                    </Flex>
                  }
                />
              </motion.div>
            )}
          </AnimatePresence>
          <List
            itemLayout="horizontal"
            dataSource={bank}
            rowKey={(item) => item.accountId}
            renderItem={(item) => (
              <List.Item>
                <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                  <Flex align="center" gap={12}>
                    <Image
                      width={16}
                      height={16}
                      src={getOrganizationLogo(item.bankCode).x1}
                      alt={BANK_CODES[item.bankCode]}
                    />
                    <Typography.Text>{item.accountNumber}</Typography.Text>
                  </Flex>
                  <Flex gap={12} align="center">
                    <Convert
                      value={item.scrapedAt}
                      expression={(scrapedAt) => (
                        <Typography.Text>
                          {dayjs(scrapedAt).format('YYYY-MM-DD HH:mm:ss')}
                        </Typography.Text>
                      )}
                    />
                    {getAnalyzeStatusTag(item.status)}
                  </Flex>
                </Flex>
              </List.Item>
            )}
          />
        </Card>
      )}
      {securityAccountList.length > 0 && (
        <Card title="증권 계좌" bordered={false}>
          <AnimatePresence mode="popLayout">
            {abnormalSecurityAccounts.length > 0 && (
              <motion.div
                key="abnormal-accounts"
                initial={{ opacity: 0, y: -24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                className={css`
                  padding-bottom: 16px;
                `}
              >
                <Alert
                  message="계좌 정보를 찾을 수 없습니다"
                  description={
                    <>
                      수집할 수 없는 계좌가 {abnormalSecurityAccounts?.length}개 있습니다.
                      <br />
                      자세히 버튼을 클릭하여 필요한 조치를 취해주세요.
                    </>
                  }
                  type="error"
                  showIcon
                  banner
                  action={
                    <Flex gap={4} align="center">
                      <Button
                        size="small"
                        onClick={() => {
                          overlays.open(({ isOpen, onClose }) => (
                            <ScrapeErrorsDrawer
                              isOpen={isOpen}
                              onClose={onClose}
                              accountType="security"
                            />
                          ));
                        }}
                      >
                        자세히
                      </Button>
                    </Flex>
                  }
                />
              </motion.div>
            )}
          </AnimatePresence>

          <List
            itemLayout="horizontal"
            dataSource={securityAccountList}
            rowKey={(item) => item.organizationCode}
            renderItem={(item) => (
              <List.Item>
                <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                  <Flex align="center" gap={12}>
                    <Image
                      width={16}
                      height={16}
                      src={getOrganizationLogo(item.organizationCode).x1}
                      alt={ShortedBankName[item.organizationCode as keyof typeof ShortedBankName]}
                    />
                    <Typography.Text>
                      {ShortedBankName[item.organizationCode as keyof typeof ShortedBankName]}
                    </Typography.Text>
                  </Flex>
                  <Flex gap={12} align="center">
                    <Convert
                      value={item.scrapedAt}
                      expression={(scrapedAt) => (
                        <Typography.Text>
                          {dayjs(scrapedAt).format('YYYY-MM-DD HH:mm:ss')}
                        </Typography.Text>
                      )}
                    />
                    {getAnalyzeStatusTag(item.status)}
                  </Flex>
                </Flex>
              </List.Item>
            )}
          />
        </Card>
      )}

      {(taxInvoiceDataExist || cashInvoiceDataExist) && (
        <ListForHometax
          title="홈택스"
          dataSource={taxInvoice
            .map((i) => ({ ...i, type: getHomeTaxEntityTypeFromTaxInvoice(i) }))
            .concat(
              cashReceipt.map((i) => ({ ...i, type: getHomeTaxEntityTypeFromCashReceipt(i) })),
            )}
          certificates={ntsCertificates}
        />
      )}

      <ListForCorpCard
        datasource={card.sort((a, b) => {
          // First sort by organization code
          if (a.organizationCode !== b.organizationCode) {
            return a.organizationCode.localeCompare(b.organizationCode);
          }
          // Then sort by type
          return CARD_SCRAPE_TYPE_ORDER.indexOf(a.type) - CARD_SCRAPE_TYPE_ORDER.indexOf(b.type);
        })}
        title={'카드'}
        connections={cardConnections}
        scrapeProps={{
          onScrape: async () => {
            if (!companyId) {
              return;
            }
            const response = await postBillingScraping(companyId as string);
            if (response.Status === ResponseStatus.SUCCESS) {
              Toast.success('청구/즉시결제 내역 수집 요청이 완료되었습니다.');
            } else {
              Toast.error('청구/즉시결제 내역 수집 요청에 실패했습니다.');
            }
          },
          title: '청구/즉시결제 재수집',
          disabled: overallStatus === 'IN_PROGRESS',
        }}
      />

      {crefiaCredential !== null && (
        <ListForCrefia
          credential={crefiaCredential}
          purchasedCardSalesList={purchasedCardSalesList}
        />
      )}
    </div>
  );
};
